import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowLeft, BookOpen, Search, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { quantListAssetPrices, quantGetAssetPrices, getErrorMessage } from '../../lib/api';
import EquityChart from './EquityChart';
import StrategyApplicability from './StrategyApplicability';

const fmtPct = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
const fmtNum = (v, d = 2) => v == null ? '—' : v.toFixed(d);

/**
 * Catalogo asset: griglia per categoria + chart standalone (no strategia).
 * Mostra OHLCV+volume + livelli (ATH, ATL, 52W) + statistiche (volume z-score, YTD).
 */
const AssetCatalog = ({ universe, classLabels, onApplyStrategy, onProposeEvolution }) => {
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [assetData, setAssetData] = useState(null);
  const [loadingAsset, setLoadingAsset] = useState(false);
  const [showVolume, setShowVolume] = useState(true);

  useEffect(() => {
    setLoadingList(true);
    quantListAssetPrices()
      .then(r => setItems(r.data.items || []))
      .catch(e => {
        if (e.response?.status !== 404) {
          toast.error(getErrorMessage(e, 'Errore caricamento catalogo'));
        }
      })
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    if (!selected) { setAssetData(null); return; }
    setLoadingAsset(true);
    quantGetAssetPrices(selected)
      .then(r => setAssetData(r.data))
      .catch(e => toast.error(getErrorMessage(e, `Errore caricamento ${selected}`)))
      .finally(() => setLoadingAsset(false));
  }, [selected]);

  // Raggruppa per asset_class. Fallback su universe statico se _index.json vuoto.
  const groups = useMemo(() => {
    const source = items.length ? items : (universe || []).map(u => ({
      symbol: u.symbol, name: u.name, asset_class: u.asset_class,
    }));
    const byClass = {};
    for (const a of source) {
      const cls = a.asset_class || 'other';
      if (!byClass[cls]) byClass[cls] = [];
      byClass[cls].push(a);
    }
    const order = Object.keys(classLabels || {});
    return Object.keys(byClass).sort((x, y) => {
      const ix = order.indexOf(x); const iy = order.indexOf(y);
      if (ix !== -1 && iy !== -1) return ix - iy;
      if (ix !== -1) return -1;
      if (iy !== -1) return 1;
      return x.localeCompare(y);
    }).map(cls => ({
      key: cls,
      label: (classLabels || {})[cls] || cls,
      items: byClass[cls].filter(a => {
        if (!filter) return true;
        const q = filter.toLowerCase();
        return a.symbol.toLowerCase().includes(q) || (a.name || '').toLowerCase().includes(q);
      }),
    })).filter(g => g.items.length);
  }, [items, universe, classLabels, filter]);

  // === VISTA DETTAGLIO (un asset selezionato) ===
  if (selected) {
    const meta = assetData ? {
      symbol: assetData.symbol,
      name: assetData.name,
      asset_class: assetData.asset_class,
      stats: assetData.stats || {},
      levels: assetData.levels || {},
      ohlcv: assetData.ohlcv || [],
    } : null;

    return (
      <Card>
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Catalogo
              </Button>
              {meta && (
                <CardTitle className="text-base flex items-center gap-2 flex-wrap min-w-0">
                  <TrendingUp className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-mono">{meta.symbol}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-sm font-normal truncate">{meta.name}</span>
                  <Badge variant="outline" className="text-[9px]">{meta.asset_class}</Badge>
                </CardTitle>
              )}
            </div>
            {onApplyStrategy && (
              <Button size="sm" variant="default" onClick={() => onApplyStrategy(meta?.symbol)}>
                Prova una strategia →
              </Button>
            )}
          </div>
          {meta && (
            <div className="text-xs text-muted-foreground mt-1.5 flex gap-3 flex-wrap">
              <span>📅 {assetData.start} → {assetData.end}</span>
              {meta.stats.n_bars && <span>📊 {meta.stats.n_bars} sedute</span>}
              {meta.stats.last_close != null && (
                <span>💲 Ultimo: {fmtNum(meta.stats.last_close)}</span>
              )}
              {meta.stats.last_change_pct != null && (
                <span className={meta.stats.last_change_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}>
                  {meta.stats.last_change_pct >= 0 ? <TrendingUp className="w-3 h-3 inline" /> : <TrendingDown className="w-3 h-3 inline" />}
                  {' '}{fmtPct(meta.stats.last_change_pct)}
                </span>
              )}
              {meta.stats.ytd_return_pct != null && (
                <span>YTD: <span className={meta.stats.ytd_return_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}>{fmtPct(meta.stats.ytd_return_pct)}</span></span>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-4">
          {loadingAsset && <div className="h-96 bg-muted animate-pulse rounded-lg" />}
          {!loadingAsset && assetData && (
            <>
              {/* Mini KPI levels */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
                {meta.levels.all_time_high && (
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">All-Time High</p>
                    <p className="font-mono font-semibold">{fmtNum(meta.levels.all_time_high.price)}</p>
                    <p className="text-[10px] text-muted-foreground">{meta.levels.all_time_high.date}</p>
                  </div>
                )}
                {meta.levels.all_time_low && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-950/30 px-3 py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">All-Time Low</p>
                    <p className="font-mono font-semibold">{fmtNum(meta.levels.all_time_low.price)}</p>
                    <p className="text-[10px] text-muted-foreground">{meta.levels.all_time_low.date}</p>
                  </div>
                )}
                {meta.levels.wk52_high && (
                  <div className="rounded-lg bg-muted/40 px-3 py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">52W High</p>
                    <p className="font-mono font-semibold">{fmtNum(meta.levels.wk52_high.price)}</p>
                  </div>
                )}
                {meta.stats.vol_z_score_last != null && (
                  <div className={`rounded-lg px-3 py-2 ${Math.abs(meta.stats.vol_z_score_last) > 2 ? 'bg-amber-50 dark:bg-amber-950/30' : 'bg-muted/40'}`}>
                    <p className="text-[10px] uppercase text-muted-foreground">Volume z-score</p>
                    <p className="font-mono font-semibold">{fmtNum(meta.stats.vol_z_score_last, 2)}σ</p>
                    {Math.abs(meta.stats.vol_z_score_last) > 2 && (
                      <p className="text-[10px] text-amber-600 dark:text-amber-400">📈 anomalia recente</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 mb-2">
                <button
                  onClick={() => setShowVolume(v => !v)}
                  className={`text-[10px] px-2 py-0.5 rounded border ${
                    showVolume ? 'bg-primary/10 text-primary border-primary/30' : 'text-muted-foreground border-muted'
                  }`}
                >
                  📊 Volumi: {showVolume ? 'ON' : 'OFF'}
                </button>
              </div>

              <EquityChart
                data={{ ohlcv: meta.ohlcv, markers: [] }}
                mode="price"
                showVolume={showVolume}
                height={460}
              />

              {/* Strategy applicability — quali strategie hanno senso su questo asset */}
              <div className="mt-5">
                <StrategyApplicability
                  symbol={meta.symbol}
                  onApplyStrategy={(stratName) => {
                    setSelected(null);
                    onApplyStrategy?.(meta.symbol, stratName);
                  }}
                  onProposeEvolution={(stratName) => {
                    onProposeEvolution?.(stratName, meta.symbol);
                  }}
                />
              </div>
            </>
          )}
          {!loadingAsset && !assetData && (
            <div className="text-sm text-muted-foreground text-center py-12">
              Asset non esportato. Lancia <code className="font-mono mx-1">python scripts/export_asset_prices.py {selected}</code> in Quant Lab.
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // === VISTA CATALOGO (griglia per categoria) ===
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary" />
            Catalogo asset
            <span className="text-xs text-muted-foreground font-normal">
              {items.length ? `${items.length} asset disponibili` : `${universe?.length || 0} ticker (vault non ancora popolato)`}
            </span>
          </CardTitle>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Cerca ticker o nome…"
              className="h-9 pl-8 text-sm w-64"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loadingList && <div className="h-40 bg-muted animate-pulse rounded-lg" />}
        {!loadingList && items.length === 0 && (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            Vault non ancora popolato. Lancia in Quant Lab:
            <pre className="mt-2 inline-block text-xs bg-muted/40 px-3 py-1 rounded font-mono">python scripts/export_asset_prices.py --all</pre>
            <p className="mt-3 text-xs">Intanto puoi navigare i 77 ticker dichiarati nell'universe (click apre il chart se esportato).</p>
          </div>
        )}
        {groups.map(g => (
          <div key={g.key}>
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-2 flex items-center gap-2">
              {g.label}
              <Badge variant="outline" className="text-[9px]">{g.items.length}</Badge>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {g.items.map(a => {
                const isExported = items.some(it => it.symbol === a.symbol);
                const stats = (items.find(it => it.symbol === a.symbol) || {}).stats || {};
                return (
                  <button
                    key={a.symbol}
                    onClick={() => setSelected(a.symbol)}
                    disabled={!isExported}
                    className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                      isExported
                        ? 'hover:border-primary/50 hover:bg-primary/5 cursor-pointer'
                        : 'opacity-50 cursor-not-allowed border-dashed'
                    }`}
                    title={!isExported ? 'Asset non ancora esportato' : `Click per il chart standalone`}
                  >
                    <p className="font-mono font-semibold text-sm">{a.symbol}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{a.name}</p>
                    {stats.last_change_pct != null && (
                      <p className={`text-[10px] mt-0.5 font-mono ${stats.last_change_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {fmtPct(stats.last_change_pct)} today
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default AssetCatalog;
